<template>
    <div class="tx-dealersearch module-search-form form">
        <div class="form-group">
            <div class="input">
                <input
                    id="location"
                    ref="input"
                    v-model="value"
                    type="text"
                    class="place-autocomplete__input"
                    :class="{ '-filled': value }"
                    :placeholder="placeholder"
                    autocomplete="off"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-controls="place-autocomplete-list"
                    :aria-expanded="offen ? 'true' : 'false'"
                    :aria-activedescendant="markiert >= 0 ? 'place-autocomplete-item-' + markiert : null"
                    @input="beiEingabe"
                    @focusin="handleFocusin"
                    @focusout="handleFocusout"
                    @keydown="beiTaste">
                <div class="input-background icon-magnifier-after">
                    <label for="location">{{ $t('dealersearch.searchField.label') }}</label>
                </div>
                <button
                    type="button"
                    class="input-action-dummy btn -btn-alpha"
                    @mousedown.prevent
                    @click="uebernimmBesten">
                    <span>{{ $t('dealersearch.searchField.action.dummy') }}</span>
                </button>
                <ul
                    v-if="offen"
                    id="place-autocomplete-list"
                    class="place-autocomplete__list"
                    role="listbox">
                    <li
                        v-for="(vorschlag, i) in vorschlaege"
                        :id="'place-autocomplete-item-' + i"
                        :key="vorschlag.schluessel"
                        class="place-autocomplete__item"
                        :class="{ '-active': i === markiert }"
                        role="option"
                        :aria-selected="i === markiert ? 'true' : 'false'"
                        @mousedown.prevent="uebernimm(vorschlag)"
                        @mousemove="markiert = i">
                        {{ vorschlag.text }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Ortssuche ohne Google.
 *
 * Ersetzt GoogleAutocomplete.vue. Nach aussen verhaelt sie sich gleich: dieselben
 * Props, dasselbe Ereignis placeChanged mit location, latitude, longitude und
 * country. Store, Filter, Trefferliste und Karte bleiben unberuehrt.
 *
 * WARUM
 *
 * Die alte Komponente haengt an google.maps.places.Autocomplete und damit an der
 * Marketing-Einwilligung. Wer die Cookies ablehnt, konnte die Ausstellungssuche
 * gar nicht benutzen - die Trefferliste stand da, aber unsortiert.
 *
 * Gebraucht wurde Google nur fuer einen Schritt: aus "94167" oder "Passau" ein
 * Koordinatenpaar zu machen. Die Entfernungssortierung rechnet seit jeher der
 * eigene Server (DealerRepository, Haversine in SQL). Diese Komponente schlaegt
 * die Koordinaten in einer mitgelieferten Ortsliste nach, und damit verlaesst
 * die Eingabe das Geraet des Besuchers nicht mehr.
 *
 * Die KARTE bleibt bewusst hinter der Einwilligung. Daran aendert sich nichts.
 *
 * GRENZEN, DIE MAN KENNEN MUSS
 *
 * - Strassenadressen werden nur bis zum Ort erkannt, nicht bis zur Hausnummer.
 *   Bei rund dreissig Standorten ohne Bedeutung.
 * - Keine Tippfehlertoleranz. "Pasau" findet nichts. Google konnte das; der
 *   Preis dafuer war eine Anfrage samt IP-Adresse bei jedem Tastendruck.
 *
 * DATENQUELLE
 *
 * Resources/Public/Data/orte-de-at.json, erzeugt von Build/plz-liste-bauen.mjs
 * aus den GeoNames-Dumps (CC BY 4.0, Namensnennung noetig).
 *
 * Die Datei wird ERST beim ersten Tastendruck geladen, nicht beim Seitenaufbau.
 * Wer die Suche nicht benutzt, laedt sie nie.
 *
 * EIN VORSCHLAG JE ORT, NICHT JE PLZ
 *
 * Die Liste hat einen Eintrag je PLZ - Berlin 182-mal, Augsburg 14-mal. Ohne
 * Zusammenfassen hiesse "Berlin" tippen: acht Mal Berlin und sonst nichts.
 * Deshalb werden beim Laden gleichnamige Eintraege zu Ortsgruppen verbunden,
 * und die Vorschlagsliste zeigt je Gruppe eine Zeile.
 *
 * Welche Eintraege EIN Ort sind, entscheidet schon das Build-Skript: es
 * vergibt gleichnamigen, weit auseinanderliegenden Orten einen Zusatz -
 * "Au (Vorarlberg)", "Neustadt (Landkreis Coburg)". Hier wird nur noch nach
 * Land, Name und Zusatz zusammengefasst.
 *
 * Die PLZ-Suche bleibt unberuehrt: "86150" findet weiter genau diese PLZ und
 * zeigt sie auch an.
 */

const MIN_ZEICHEN = 2
const MAX_VORSCHLAEGE = 8

/**
 * Vergleichsform: Kleinbuchstaben, Umlaute aufgeloest, alles weg, was nicht
 * Buchstabe oder Ziffer ist. Damit findet "muenchen" dasselbe wie "München"
 * und "Sankt Poelten" dasselbe wie "St.Pölten" nicht - aber Umlaute und
 * Bindestriche sind die Faelle, die in der Praxis vorkommen.
 */
function vergleichsform (text) {
  return String(text)
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '')
}

/**
 * Fasst die Eintraege gleichen Landes, Namens und Zusatzes zu einem Ort
 * zusammen und haengt jedem Eintrag seinen Ort an (eintrag.gruppe). Der
 * Mittelpunkt des Ortes ist der Durchschnitt seiner PLZ-Koordinaten.
 */
function bildeOrtsgruppen (orte) {
  const gruppen = new Map()
  for (const ort of orte) {
    const schluessel = ort.land + ':' + ort.ortForm + ':' + ort.zusatz
    let gruppe = gruppen.get(schluessel)
    if (!gruppe) {
      gruppe = {
        schluessel,
        name: ort.ort,
        zusatz: ort.zusatz,
        land: ort.land,
        lat: 0,
        lng: 0,
        anzahl: 0
      }
      gruppen.set(schluessel, gruppe)
    }
    gruppe.lat += ort.lat
    gruppe.lng += ort.lng
    gruppe.anzahl++
    ort.gruppe = gruppe
  }
  for (const gruppe of gruppen.values()) {
    gruppe.lat /= gruppe.anzahl
    gruppe.lng /= gruppe.anzahl
  }
}

/** 'AT' auf /at/..., sonst 'DE'. */
function bevorzugtesLand () {
  const locale = typeof window !== 'undefined' && window.app && window.app.dealersearch_list
    ? String(window.app.dealersearch_list.locale || '')
    : ''
  return /-AT$/i.test(locale) ? 'AT' : 'DE'
}

export default {
  name: 'PlaceAutocomplete',
  inject: ['plzListUrl'],
  props: {
    placeholder: {
      type: String,
      default: ''
    },
    location: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      value: '',
      focus: false,
      orte: null,
      ladevorgang: null,
      vorschlaege: [],
      markiert: -1
    }
  },
  computed: {
    offen () {
      return this.focus && this.vorschlaege.length > 0
    }
  },
  methods: {
    /**
     * Laedt die Ortsliste einmalig. Mehrfache Aufrufe waehrend des Ladens
     * teilen sich dasselbe Versprechen; ein Fehlschlag wird nicht gemerkt,
     * damit der naechste Tastendruck es erneut versuchen kann.
     */
    ladeOrte () {
      if (this.orte) return Promise.resolve(this.orte)
      if (this.ladevorgang) return this.ladevorgang

      const ladevorgang = fetch(this.plzListUrl)
        .then(antwort => {
          if (!antwort.ok) throw new Error('HTTP ' + antwort.status)
          return antwort.json()
        })
        .then(inhalt => {
          const roh = Array.isArray(inhalt && inhalt.orte) ? inhalt.orte : []
          const zusaetze = Array.isArray(inhalt.zusaetze) ? inhalt.zusaetze : []
          const orte = roh.map(([plz, ort, lat, lng, land, zusatz]) => ({
            plz,
            ort,
            lat,
            lng,
            land,
            zusatz: zusaetze[zusatz] || '',
            plzForm: vergleichsform(plz),
            ortForm: vergleichsform(ort)
          }))
          bildeOrtsgruppen(orte)
          this.orte = orte
          return this.orte
        })
        .catch(() => {
          // Still bleiben: ohne Liste gibt es eben keine Vorschlaege. Eine
          // Fehlermeldung an dieser Stelle haette der Besucher gegen sich,
          // nicht fuer sich.
          this.orte = null
          return null
        })
        .finally(() => {
          if (this.ladevorgang === ladevorgang) this.ladevorgang = null
        })

      this.ladevorgang = ladevorgang
      return ladevorgang
    },

    /**
     * Rangfolge, absichtlich simpel: genaue PLZ zuerst, dann PLZ-Anfang, dann
     * genauer Ortsname, dann Ortsname-Anfang, zuletzt Ortsname irgendwo. Der
     * genaue Name hat eine eigene Stufe, damit "Wien" auf der DE-Seite nicht
     * hinter "Wiendorf" landet. Innerhalb derselben Stufe
     * kommen Orte des eigenen Landes zuerst (auf /at/ also Oesterreich), dann
     * der kuerzere Ortsname - "Passau" vor "Passau-Grubweg" -, dann die
     * groessere Gruppe, damit die Stadt vor dem gleichnamigen Dorf steht.
     *
     * Das Land sortiert nur, es filtert nicht: grenznahe Besucher sollen die
     * Orte im Nachbarland weiter finden.
     *
     * Je Ortsgruppe ein Vorschlag, mit dem besten Treffer der Gruppe. Passt
     * die PLZ, steht sie in der Beschriftung und deren Koordinaten zaehlen;
     * passt nur der Name, steht der Ort fuer sich mit dem Mittelpunkt der
     * Gruppe.
     */
    suche (eingabe) {
      if (!this.orte) return []
      const form = vergleichsform(eingabe)
      if (form.length < MIN_ZEICHEN) return []

      const besterJeGruppe = new Map()
      for (const ort of this.orte) {
        let stufe = -1
        if (ort.plzForm === form) stufe = 0
        else if (ort.plzForm.startsWith(form)) stufe = 1
        else if (ort.ortForm === form) stufe = 2
        else if (ort.ortForm.startsWith(form)) stufe = 3
        else if (ort.ortForm.includes(form)) stufe = 4
        if (stufe < 0) continue
        const bisher = besterJeGruppe.get(ort.gruppe)
        if (!bisher || stufe < bisher.stufe) besterJeGruppe.set(ort.gruppe, { ort, stufe })
      }

      const land = bevorzugtesLand()
      const treffer = [...besterJeGruppe.values()]
      treffer.sort((a, b) =>
        a.stufe - b.stufe ||
        (a.ort.land === land ? 0 : 1) - (b.ort.land === land ? 0 : 1) ||
        a.ort.gruppe.name.length - b.ort.gruppe.name.length ||
        b.ort.gruppe.anzahl - a.ort.gruppe.anzahl)

      return treffer.slice(0, MAX_VORSCHLAEGE).map(({ ort, stufe }) => {
        const gruppe = ort.gruppe
        if (stufe <= 1) {
          return {
            schluessel: gruppe.schluessel,
            text: ort.plz + ' ' + ort.ort,
            lat: ort.lat,
            lng: ort.lng,
            land: ort.land
          }
        }
        return {
          schluessel: gruppe.schluessel,
          text: gruppe.zusatz ? gruppe.name + ' (' + gruppe.zusatz + ')' : gruppe.name,
          lat: gruppe.lat,
          lng: gruppe.lng,
          land: gruppe.land
        }
      })
    },

    aktualisiereVorschlaege () {
      this.vorschlaege = this.suche(this.value)
      this.markiert = this.vorschlaege.length > 0 ? 0 : -1
    },

    beiEingabe () {
      if (vergleichsform(this.value).length < MIN_ZEICHEN) {
        this.vorschlaege = []
        this.markiert = -1
        return
      }
      this.ladeOrte().then(() => {
        // Zwischenzeitlich kann weitergetippt oder das Feld geleert worden
        // sein - deshalb hier erneut gegen den AKTUELLEN Wert suchen.
        this.aktualisiereVorschlaege()
      })
    },

    beiTaste (ereignis) {
      if (ereignis.key === 'ArrowDown' || ereignis.key === 'ArrowUp') {
        if (this.vorschlaege.length === 0) return
        ereignis.preventDefault()
        const richtung = ereignis.key === 'ArrowDown' ? 1 : -1
        const anzahl = this.vorschlaege.length
        this.markiert = (this.markiert + richtung + anzahl) % anzahl
        return
      }
      if (ereignis.key === 'Enter') {
        if (this.vorschlaege.length === 0) return
        ereignis.preventDefault()
        this.uebernimm(this.vorschlaege[Math.max(this.markiert, 0)])
        return
      }
      if (ereignis.key === 'Escape') {
        this.vorschlaege = []
        this.markiert = -1
      }
    },

    uebernimmBesten () {
      if (this.vorschlaege.length > 0) {
        this.uebernimm(this.vorschlaege[Math.max(this.markiert, 0)])
        return
      }
      // Knopf gedrueckt, ohne dass Vorschlaege dastehen: nachladen, suchen,
      // und wenn genau etwas passt, danach suchen.
      this.ladeOrte().then(() => {
        const treffer = this.suche(this.value)
        if (treffer.length > 0) this.uebernimm(treffer[0])
      })
    },

    uebernimm (vorschlag) {
      if (!vorschlag) return
      this.value = vorschlag.text
      this.vorschlaege = []
      this.markiert = -1
      this.$emit('placeChanged', {
        location: this.value,
        latitude: Number(vorschlag.lat.toFixed(4)),
        longitude: Number(vorschlag.lng.toFixed(4)),
        country: vorschlag.land
      })
    },

    handleFocusin () {
      this.focus = true
    },

    /**
     * Gleiches Verhalten wie zuvor: steht im Feld etwas, das nicht zur
     * gewaehlten Ortsangabe passt, wird es verworfen und die Suche
     * zurueckgesetzt. Sonst suchte die Seite weiter nach einem Ort, der im
     * Feld gar nicht mehr steht.
     */
    handleFocusout () {
      this.focus = false
      this.vorschlaege = []
      this.markiert = -1
      if (this.location !== this.value) {
        this.value = ''
        this.$emit('placeChanged', {
          location: '',
          latitude: '',
          longitude: '',
          country: ''
        })
      }
    }
  },
  mounted () {
    this.value = this.location
  },
  beforeUpdate () {
    if (!this.focus && this.location !== this.value) {
      this.value = this.location
    }
  }
}
</script>
